import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, Plus, Minus, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, subtotal, shipping, total, count } =
    useCart();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm"
            data-testid="cart-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 bottom-0 z-[81] w-full sm:w-[440px] bg-[#050505] border-l border-white/10 flex flex-col"
            data-testid="cart-drawer"
          >
            <header className="p-6 flex items-center justify-between border-b border-white/10">
              <div>
                <p className="eyebrow">Your Cart · {count}</p>
                <h3 className="font-serif text-2xl text-white mt-1">Shopping bag</h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="cart-close"
                className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors"
                aria-label="Close cart"
              >
                <X size={16} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto p-6" data-lenis-prevent>
              {items.length === 0 ? (
                <div className="py-20 text-center">
                  <p className="font-serif text-2xl text-neutral-500">
                    Your cart is empty.
                  </p>
                  <Link
                    to="/shop"
                    onClick={() => setOpen(false)}
                    className="mt-6 inline-block font-mono text-[11px] tracking-widest uppercase text-neutral-400 hover:text-white"
                  >
                    Browse the shop →
                  </Link>
                </div>
              ) : (
                <ul className="space-y-6">
                  {items.map((it) => (
                    <li key={it.slug} className="flex gap-4" data-testid={`cart-item-${it.slug}`}>
                      <div className="w-20 h-20 shrink-0 bg-[#0a0a0a] overflow-hidden">
                        <img src={it.image} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-white text-sm leading-snug">{it.name}</p>
                        <p className="mt-1 font-mono text-[10px] tracking-widest uppercase text-neutral-500">
                          ${it.price.toFixed(2)} ea
                        </p>
                        <div className="mt-3 flex items-center gap-2">
                          <button
                            onClick={() => setQty(it.slug, it.quantity - 1)}
                            data-testid={`cart-dec-${it.slug}`}
                            className="w-7 h-7 border border-white/15 text-white hover:bg-white hover:text-black transition-colors flex items-center justify-center"
                          >
                            <Minus size={12} />
                          </button>
                          <span className="w-8 text-center text-white text-sm">{it.quantity}</span>
                          <button
                            onClick={() => setQty(it.slug, it.quantity + 1)}
                            data-testid={`cart-inc-${it.slug}`}
                            className="w-7 h-7 border border-white/15 text-white hover:bg-white hover:text-black transition-colors flex items-center justify-center"
                          >
                            <Plus size={12} />
                          </button>
                          <button
                            onClick={() => remove(it.slug)}
                            data-testid={`cart-remove-${it.slug}`}
                            className="ml-auto font-mono text-[10px] tracking-widest uppercase text-neutral-500 hover:text-brand-red"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <div className="text-white font-serif text-lg">
                        ${(it.price * it.quantity).toFixed(2)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <footer className="p-6 border-t border-white/10 space-y-4">
                <div className="flex justify-between text-neutral-400 text-sm">
                  <span>Subtotal</span>
                  <span className="text-white">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-neutral-400 text-sm">
                  <span>Shipping</span>
                  <span className="text-white">{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between items-baseline pt-4 border-t border-white/10">
                  <span className="eyebrow">Total AUD</span>
                  <span className="font-serif text-3xl text-white">${total.toFixed(2)}</span>
                </div>
                <Link
                  to="/checkout"
                  onClick={() => setOpen(false)}
                  data-testid="cart-checkout-btn"
                  className="btn-primary w-full justify-center"
                >
                  Checkout <ArrowRight size={14} />
                </Link>
              </footer>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
